/**
 * A skill's weekly loads as a small line, oldest week on the left; the
 * latest week ends in a dot. A skill never loaded in the window draws a faint
 * floor, so the column reads "unused" rather than "missing".
 *
 * @module components/skills/SkillSparkline
 */
import { cn } from "../../lib/utils";
import { sparklinePath } from "./skillBoardLogic";

export function SkillSparkline({
  values,
  width = 72,
  height = 22,
  className,
}: {
  readonly values: ReadonlyArray<number>;
  readonly width?: number;
  readonly height?: number;
  readonly className?: string;
}) {
  const used = values.some((value) => value > 0);
  const path = sparklinePath(values, width, height);
  const max = Math.max(...values, 0);
  const last = values.at(-1) ?? 0;
  const lastY = max === 0 ? height - 1.5 : 1.5 + (height - 3) * (1 - last / max);
  return (
    <svg
      aria-hidden
      width={width}
      height={height}
      viewBox={`-2 -2 ${width + 4} ${height + 4}`}
      className={cn(
        "shrink-0 overflow-visible",
        used ? "text-foreground/70" : "text-foreground/15",
        className,
      )}
    >
      {used ? (
        <path d={`${path} L${width} ${height} L0 ${height} Z`} fill="currentColor" opacity={0.08} />
      ) : null}
      <path
        d={path}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {used ? <circle cx={width} cy={lastY} r={2} fill="currentColor" /> : null}
    </svg>
  );
}
